import React, { useState } from 'react';

const EnhancedTranscript = ({
    utterances,
    detectedLanguage,
    languageConfidence,
    speakerLabelsMap,
    onRename
}) => {
    const [selectedSpeaker, setSelectedSpeaker] = useState(null);
    const [newName, setNewName] = useState('');

    // Format milliseconds to MM:SS
    const formatTime = (ms) => {
        const seconds = Math.floor(ms / 1000);
        const min = Math.floor(seconds / 60);
        const sec = seconds % 60;
        return `${min}:${sec.toString().padStart(2, '0')}`;
    };

    // Get speaker display name
    const getSpeakerName = (speaker) => {
        return speakerLabelsMap?.[speaker] || `Speaker ${speaker}`;
    };

    // Color code speakers
    const getSpeakerColor = (speaker) => {
        const colors = {
            'A': '#0d6efd',
            'B': '#198754',
            'C': '#ffc107',
            'D': '#dc3545'
        };
        return colors[speaker] || '#6c757d';
    };

    const handleRename = () => {
        if (newName.trim() && onRename) {
            onRename(selectedSpeaker, newName.trim());
            setSelectedSpeaker(null);
            setNewName('');
        }
    };

    const handleClose = () => {
        setSelectedSpeaker(null);
        setNewName('');
    };

    return (
        <>
            <div className="card mt-3">
                <div className="card-header d-flex justify-content-between align-items-center">
                    <strong>Final Transcript</strong>
                    {detectedLanguage && (
                        <span className="badge bg-info">
                            Language: {detectedLanguage.toUpperCase()}
                            {languageConfidence && ` (${Math.round(languageConfidence * 100)}%)`}
                        </span>
                    )}
                </div>
                <div className="card-body" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                    {utterances && utterances.length > 0 ? (
                        utterances.map((utt, idx) => (
                            <div key={idx} className="mb-2">
                                <small className="text-muted">[{formatTime(utt.start)}]</small>
                                <strong
                                    className="ms-2 speaker-label"
                                    style={{
                                        cursor: onRename ? 'pointer' : 'default',
                                        color: getSpeakerColor(utt.speaker),
                                        textDecoration: onRename ? 'underline dotted' : 'none'
                                    }}
                                    onClick={() => onRename && setSelectedSpeaker(utt.speaker)}
                                    title={onRename ? "Click to rename speaker" : ""}
                                >
                                    {getSpeakerName(utt.speaker)}:
                                </strong>
                                <span className="ms-2">{utt.text}</span>
                            </div>
                        ))
                    ) : (
                        <p className="text-muted mb-0">No transcript available yet.</p>
                    )}
                </div>
            </div>

            {/* Speaker Rename Modal */}
            {onRename && selectedSpeaker && (
                <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Rename Speaker {selectedSpeaker}</h5>
                                <button type="button" className="btn-close" onClick={handleClose}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label">New Name:</label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        placeholder="e.g., Dr. Smith"
                                        value={newName}
                                        onChange={(e) => setNewName(e.target.value)}
                                        onKeyPress={(e) => {
                                            if (e.key === 'Enter') {
                                                handleRename();
                                            }
                                        }}
                                        autoFocus
                                    />
                                </div>
                                <div className="d-flex gap-2 flex-wrap">
                                    <button className="btn btn-outline-primary btn-sm" onClick={() => setNewName('Doctor')}>
                                        Doctor
                                    </button>
                                    <button className="btn btn-outline-success btn-sm" onClick={() => setNewName('Patient')}>
                                        Patient
                                    </button>
                                    <button className="btn btn-outline-info btn-sm" onClick={() => setNewName('Nurse')}>
                                        Nurse
                                    </button>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={handleClose}>
                                    Cancel
                                </button>
                                <button type="button" className="btn btn-primary" onClick={handleRename}>
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default EnhancedTranscript;
